import React, { useState, useEffect } from 'react';
import { YieldHeatmapGrid, PercentileFilter, LegendBar } from '../components/YieldBenchmarking';
import { yieldService, YieldGridData } from '../services/yieldService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MetricCard } from '../components/ui/MetricCard';

export const YieldComparison: React.FC = () => {
  const [data, setData] = useState<YieldGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPercentile, setSelectedPercentile] = useState(95);
  const [selectedPlant, setSelectedPlant] = useState('');

  useEffect(() => {
    fetchYieldData();
  }, [selectedDate, selectedPercentile, selectedPlant]);

  const fetchYieldData = async () => {
    try {
      setLoading(true);
      const response = await yieldService.getYieldGridData(
        selectedDate,
        selectedPercentile,
        selectedPlant || undefined
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching yield data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (blockId: string, inverterId: string, yieldValue: number) => {
    console.log(`Clicked: ${blockId} - ${inverterId}: ${yieldValue} kWh/kWp`);
    // TODO: Implement drill-down functionality
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Inverters Specific Yield Comparison</h1>
        
        <div className="flex gap-4 items-center">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />
          <PercentileFilter
            selectedPercentile={selectedPercentile}
            onPercentileChange={setSelectedPercentile}
          />
          <select
            value={selectedPlant}
            onChange={(e) => setSelectedPlant(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">All Plants</option>
            <option value="1">PSPR_Hindupur</option>
          </select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Total Records"
              value={data.summary.total_records}
              unit=""
              trend="neutral"
            />
            <MetricCard
              title="Average Yield"
              value={data.summary.avg_yield}
              unit="kWh/kWp"
              trend="neutral"
            />
            <MetricCard
              title={`${selectedPercentile}th Percentile`}
              value={data.summary.percentile_value}
              unit="kWh/kWp"
              trend="up"
            />
            <MetricCard
              title="Yield Range"
              value={`${data.summary.min_yield} - ${data.summary.max_yield}`}
              unit="kWh/kWp"
              trend="neutral"
            />
          </div>

          <LegendBar percentileBands={data.percentile_bands} />

          <YieldHeatmapGrid 
            data={data}
            onCellClick={handleCellClick}
          />
        </>
      )}
    </div>
  );
};